import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import PdfViewerDialog from 'components/ReusableComponents/PdfViewerDialog'
import Toggle from 'components/ReusableComponents/Toggle'
import ChainLogo from 'features/NewHotelDialog/ChainLogo'
import useForm from 'hooks/useForm'
import { parentChainNames, PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import { useToasts } from 'react-toast-notifications'
import hotelApi from 'services/api/hotel'
import subsidiariesApi from 'services/api/subsidiaries'
import userApi from 'services/api/user'
import { useChainOptions } from 'utils/manageHotel/chainsHelper'

import { getSubsidiaryList } from '../../actions/index'
import BookingEngine from './BookingEngine/index'
import styles from './NewHotel.module.scss'

const NewHotelDialog = ({
  onClose,
  onAdded,
  hotelName,
  city,
  stateCode,
  countryCode,
  postalCode,
  streetAddress,
  placesId,
  merchantId,
  contractDocuments
}) => {
  const [formData, handleFormChange, setFormData] = useForm({
    merchantId,
    parentChainCode: '',
    chainCode: '',
    hotelName,
    bookingEngineLink: '',
    externalHotelId: '',
    bookingEngineSlug: '',
    bookingEngine: '',
    pms: '',
    city,
    stateCode,
    countryCode,
    numberOfRooms: '',
    postalCode,
    streetAddress,
    placesId,
    salesPersonUserId: '',
    referrerId: null,
    newReferrerName: null,
    contractId: '',
    monthlyRate: 0,
    quarterlyRate: 0,
    annualRate: 0,
    paymentPeriod: 'monthly',
    isAutoPayEnabled: false,
    serviceStartDate: moment().add(9, 'days').format('YYYY-MM-DD')
  })

  const { addToast } = useToasts()

  const [chains, setParent] = useChainOptions()

  const [chainName, setChainName] = useState()
  const [brand, setBrand] = useState('')

  const [referrers, setReferrers] = useState()

  const dispatch = useDispatch()

  useEffect(() => {
    setFormData({ chainCode: brand || chainName })
  }, [chainName, brand, setFormData])

  function addNewHotel() {
    const {
      parentChainCode,
      chainCode,
      pms,
      numberOfRooms,
      salesPersonUserId,
      csmId,
      contractId,
      monthlyRate,
      quarterlyRate,
      annualRate
    } = formData

    const annualRateNumber = Number(annualRate)
    const monthlyRateNumber = Number(monthlyRate)
    const quarterlyRateNumber = Number(quarterlyRate)

    if (!salesPersonUserId) {
      addToast('Please select sales agent', { appearance: 'error' })
      return
    }

    if (!csmId) {
      addToast('Please select CSM', { appearance: 'error' })
      return
    }

    if (annualRateNumber === 0 && monthlyRateNumber === 0 && quarterlyRateNumber === 0) {
      addToast('Rates cannot be zero', { appearance: 'error' })
      return
    }

    if (quarterlyRateNumber > monthlyRateNumber) {
      addToast('Quarterly rate cannot be greater than monthly rate', {
        appearance: 'error'
      })
      return
    }

    if (annualRateNumber > monthlyRateNumber) {
      addToast('Annual rate cannot be greater than monthly rate', { appearance: 'error' })
      return
    }

    if (annualRateNumber > quarterlyRateNumber) {
      addToast('Annual rate cannot be greater than quarterly rate', {
        appearance: 'error'
      })
      return
    }

    if (
      (parentChainCode &&
        chainCode &&
        pms &&
        numberOfRooms &&
        salesPersonUserId &&
        csmId &&
        contractId,
      numberOfRooms > 0)
    ) {
      const formDataWithoutEmptyFields = Object.keys(formData).reduce((acc, curr) => {
        if (
          formData[curr] !== '' &&
          formData[curr] !== null &&
          formData[curr] !== undefined
        ) {
          return { ...acc, [curr]: formData[curr] }
        }
        return acc
      }, {})

      hotelApi
        .addHotel(formDataWithoutEmptyFields)
        .then(res => {
          if (res) {
            addToast('Hotel added successfully', { appearance: 'success' })
            dispatch(getSubsidiaryList())
            onAdded(res)
            onClose()
          }
        })
        .catch(err => {
          addToast(`Something went wrong: ${err.toString()}`, { appearance: 'error' })
        })
    } else {
      addToast('Please fill out all fields', { appearance: 'error' })
    }
  }

  function handleParentChange(e) {
    setParent(e.target.value)
    handleFormChange(e)
  }

  const [salesAgents, setSalesAgents] = useState([])

  useEffect(() => {
    userApi.getSalesAgents().then(res => {
      setSalesAgents(res)
    })
  }, [])

  const [csmUsers, setCsmUsers] = useState([])

  useEffect(() => {
    userApi.getCustomerSuccessManagers().then(res => {
      setCsmUsers(res)
    })
  }, [])

  useEffect(() => {
    subsidiariesApi.referrers.get().then(res => {
      setReferrers(res.map(r => ({ value: r.id, label: r.name })))
    })
  }, [])

  useEffect(() => {
    if (!formData.contractId) {
      setFormData({ contractId: contractDocuments[0]?.documentId })
    }
  }, [contractDocuments, formData.contractId, setFormData])

  useEffect(() => {
    if (formData.paymentPeriod === 'monthly') {
      setFormData({
        isAutoPayEnabled: true
      })
    }
  }, [formData.paymentPeriod, setFormData])

  useEffect(() => {
    if (formData.parentChainCode === 'independent' && formData.chainCode !== 'i') {
      setFormData({ chainCode: 'i' })
    }
  }, [formData.chainCode, formData.parentChainCode, setFormData])

  const [isPdfDialogVisible, setIsPdfDialogVisible] = useState(false)

  if (isPdfDialogVisible) {
    const path = contractDocuments.find(
      d => d.documentId === Number(formData.contractId)
    )?.path

    return <PdfViewerDialog onClose={() => setIsPdfDialogVisible(false)} pdfUrl={path} />
  }

  const handleChangeReferer = (newValue, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      setFormData({ newReferrerName: newValue.value, referrerId: null })
      return
    }

    if (actionMeta.action === 'clear') {
      setFormData({ referrerId: null, newReferrerName: null })
      return
    }

    if (actionMeta.action === 'select-option') {
      setFormData({ referrerId: newValue.value, newReferrerName: null })
    }
  }

  function checkDate(serviceStartDate) {
    const date = moment(serviceStartDate).format('YYYY-MM-DD')
    const selectMonthName = moment(date).format('MMMM')
    const nextMonthName = moment(date).add(1, 'months').format('MMMM')
    const todayDay = moment(date).date()
    const diffDays = moment(date).diff(moment().format('YYYY-MM-DD'), 'days')

    if (todayDay > 14 && diffDays < 30) {
      addToast(
        `A fee for ${selectMonthName} will be charged from the hotel. Consider choosing ${nextMonthName} as the start date.`,
        { appearance: 'error' }
      )
    }
    if (diffDays < 10) {
      addToast(
        ' It will take approximately 2 weeks to complete the campaign registration and participation process. Consider choosing a date at least 10 days from now.',
        { appearance: 'error' }
      )
    }
    setFormData({ serviceStartDate: serviceStartDate })
  }
  return (
    <ConfirmationDialog
      title="Add new hotel"
      maxWidth={800}
      hasHeader={false}
      theme="blacklist"
      contentPadding="0px"
      positiveMessage="Save hotel"
      negativeMessage="Cancel"
      onConfirm={addNewHotel}
      onClose={onClose}
    >
      <div className={styles.header}>
        {formData.parentChainCode && formData.parentChainCode !== 'independent' && (
          <ChainLogo
            className={styles.chainLogo}
            parentChainCode={formData.parentChainCode}
            brand={brand}
            setBrand={setBrand}
            chainCode={chainName}
          />
        )}
        <div className={styles.headerText}>
          <div className={styles.headerTitle}>{formData.hotelName}</div>
          <div className={styles.headerSubtitle}>
            {formData.streetAddress}, {formData.city}, {formData.stateCode}
          </div>
        </div>
      </div>
      <div className={styles.wrapper}>
        <div className={styles.formRow}>
          <Dropdown
            label="Sales agent"
            defaultVal={'Select sales agent'}
            options={salesAgents?.reduce(
              (acc, curr) => ({ ...acc, [curr.id]: curr.fullName }),
              {}
            )}
            onChange={handleFormChange}
            name="salesPersonUserId"
            value={formData.salesPersonUserId}
          />
          <Dropdown
            label="Customer success manager"
            defaultVal={'Select CSM'}
            options={csmUsers?.reduce(
              (acc, curr) => ({ ...acc, [curr.id]: curr.fullName }),
              {}
            )}
            onChange={handleFormChange}
            name="csmId"
            value={formData.csmId}
          />
          <div className={styles.creatableSelectContainer}>
            <div className={styles.label}>Select Referer</div>
            <CreatableSelect onChange={handleChangeReferer} options={referrers} />
          </div>
        </div>
        <div className={styles.formRow}>
          <Dropdown
            options={parentChainNames}
            label="Brand"
            defaultVal="Select parent"
            value={formData.parentChainCode}
            name="parentChainCode"
            onChange={handleParentChange}
          />
          {formData.parentChainCode !== 'independent' && (
            <Dropdown
              label="Chain"
              disabled={!formData.parentChainCode}
              defaultVal={
                formData.parentChainCode ? 'Select chain' : 'Select parent first'
              }
              options={chains}
              name="chainCode"
              value={chainName}
              onChange={e => setChainName(e.target.value)}
            />
          )}
          <Dropdown
            options={PMSnames}
            label="PMS"
            defaultVal="Select PMS"
            name="pms"
            onChange={handleFormChange}
            value={formData.pms}
          />
        </div>
        <div className={styles.formRow}>
          <TextInput
            type="number"
            label="Number of rooms"
            className={styles.input}
            name="numberOfRooms"
            value={formData.numberOfRooms}
            onChange={e => setFormData({ numberOfRooms: Number(e.target.value) })}
          />
          <TextInput
            type="date"
            label="Service start date"
            className={styles.input}
            min={moment().add(9, 'days').format('YYYY-MM-DD')}
            name="serviceStartDate"
            value={formData.serviceStartDate}
            onChange={e => checkDate(e.target.value)}
          />
        </div>
        <div className={styles.formRow}>
          <Dropdown
            label="Payment period"
            defaultVal="Monthly"
            options={{
              monthly: 'Monthly',
              quarterly: 'Quarterly',
              annual: 'Annual'
            }}
            name="paymentPeriod"
            onChange={handleFormChange}
            value={formData.paymentPeriod}
          />
          <div>
            <Toggle
              className={styles.toggle}
              isGray={formData.paymentPeriod === 'monthly'}
              disabled={formData.paymentPeriod === 'monthly'}
              checked={formData.isAutoPayEnabled}
              label="Use automatic payments"
              handleChange={() =>
                setFormData({ isAutoPayEnabled: !formData.isAutoPayEnabled })
              }
            />
            {formData.isAutoPayEnabled && (
              <div className={styles.autoPayText}>
                Using this prevents paying by physical check
              </div>
            )}
          </div>
        </div>
        <div className={styles.formRow}>
          <TextInput
            type="number"
            label="Monthly rate (USD / month)"
            value={formData.monthlyRate}
            onChange={e => setFormData({ monthlyRate: e.target.value })}
          />
          <TextInput
            type="number"
            label="Quarterly rate (USD / month)"
            value={formData.quarterlyRate}
            onChange={e => setFormData({ quarterlyRate: e.target.value })}
          />
          <TextInput
            type="number"
            label="Annual rate (USD / month)"
            value={formData.annualRate}
            onChange={e => setFormData({ annualRate: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <Dropdown
            label="Legal contract"
            defaultVal="Select contract"
            options={contractDocuments.reduce(
              (acc, curr) => ({ ...acc, [curr.documentId]: curr.name }),
              {}
            )}
            name="contractId"
            onChange={handleFormChange}
            value={formData.contractId}
          />
          {formData.contractId && (
            <SecondaryRegularButton
              className={styles.viewContractButton}
              onClick={() => setIsPdfDialogVisible(true)}
            >
              View contract
            </SecondaryRegularButton>
          )}
        </div>
        <div className={styles.optionalFields}>
          <BookingEngine
            formData={formData}
            handleFormChange={handleFormChange}
            setFormData={setFormData}
          />
        </div>
      </div>
    </ConfirmationDialog>
  )
}

export default NewHotelDialog
